import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { LiveEventContext } from '../context/Event';
import { useAttendeeData, useIsLiveEvent } from '../hooks';
import { getEventInfoFromOrder } from '../lib/orders';
import { getTimeUntilStart } from '../lib/dates';

import Notification from '../components/Notification';
import NotificationRenderer from '../components/NotificationRenderer';
import AttendeeTicketNotificationContent from '../components/AttendeeTicketNotificationContent';

import './AttendeeTicketNotification.scss';

const shouldBannerRender = (localizedEventStart, hasOrdersAndisLiveEvent) => {
    if (!localizedEventStart) {
        return false;
    }

    return localizedEventStart || hasOrdersAndisLiveEvent;
};

export const AttendeeTicketNotification = (props) => {
    const [shouldRender, setRender] = useState(true);
    const userData = useAttendeeData({
        hoursAheadPreview: props.hoursAheadPreview || 6,
        isAuthenticated: props.isAuthenticated,
    });

    const [upcomingOrder = {}] = userData ? userData.userOrders : [];
    const {
        eventId,
        ctaLinkUrl,
        eventName,
        hasDigitalContent,
        start: localizedEventStart,
    } = getEventInfoFromOrder(upcomingOrder);

    const isEventLive = useIsLiveEvent(localizedEventStart);

    /* If the event goes live. Set render state to true. This handles the
    case where the user dismisses the component but continues to stay on
    the page. */
    useEffect(() => {
        if (isEventLive) {
            setRender(true);
        }
    }, [isEventLive]);

    if (!shouldRender || !localizedEventStart) {
        return null;
    }

    const handleClose = () => {
        setRender(false);
        props.onDismiss &&
            props.onDismiss(
                getTimeUntilStart(localizedEventStart),
                hasDigitalContent,
            );
    };

    const handleLinkClick = () => {
        props.onLinkClick && props.onLinkClick(eventId, hasDigitalContent);
    };

    return (
        <LiveEventContext.Provider value={isEventLive}>
            <NotificationRenderer
                domNode={props.domeNode}
                domNodeId={props.domNodeId}
            >
                <Notification
                    shouldAnimateEntry={props.shouldAnimateEntry}
                    animateEntryDirection={props.animateEntryDirection}
                    containerClasses={
                        !hasDigitalContent
                            ? `notification__physical-event ${props.containerClasses}`
                            : props.containerClasses
                    }
                    containerStyles={props.containerStyles}
                    onClose={handleClose}
                    isNotDismissable={props.isNotDismissable}
                >
                    <AttendeeTicketNotificationContent
                        eventStart={localizedEventStart}
                        ctaLinkUrl={ctaLinkUrl}
                        hasDigitalContent={hasDigitalContent}
                        eventName={eventName}
                        onLinkClick={handleLinkClick}
                    />
                </Notification>
            </NotificationRenderer>
        </LiveEventContext.Provider>
    );
};

AttendeeTicketNotification.propTypes = {
    /*
     * Classes that will be applied to
     * the notification directly.
     */
    containerClasses: PropTypes.string,
    /*
     * Inline styles that will be applied
     * directly to the notification component.
     *
     * Useful for changing right or top offset
     * as your application needs
     */
    containerStyles: PropTypes.object,
    /*
     * Number of hours ahead an event
     * start that the notification should
     * be displayed. Defaults to 6
     */
    hoursAheadPreview: PropTypes.number,
    /*
     * If passed in, will render the notification
     * direclty as a child of the passed in dom
     * node.
     *
     * Value determined by domNodeId will take
     * precedence if both are defined.
     */
    domNode: PropTypes.node,
    /*
     * If passed in, will query the dom for
     * the ID and render the notification as
     * a direct child.
     *
     * domNodeId takes precedence if defined
     */
    domNodeId: PropTypes.string,
    /*
     * Makes it impossible to close the
     * modal and dismiss.
     */
    isNotDismissable: PropTypes.bool,
    /*
     * Optional boolean the consumer
     * can hydrate to prevent a
     * potentially unecessary api
     * call.
     */
    isAuthenticated: PropTypes.bool,
    /*
     * Callback triggered when
     * a user clicks on the CTA link
     *
     * Returns the EID of the active event
     */
    onLinkClick: PropTypes.func,
    /*
     * Callback triggered when the user
     * dismisses the notification
     *
     * Returns a diff of the current time
     * and event start time (ie time until start)
     */
    onDismiss: PropTypes.func,
    /*
     * Boolean which dictates if
     * the package should animate upon
     * mounting on the dom
     */
    shouldAnimateEntry: PropTypes.bool,
    /*
     * The direction that the notification should animate
     * upon mount.
     *
     * "left": animate moving from right to left
     * "right": animate moving left to right
     * "upwards": animate moving up
     * "downwards": animate moving down
     *
     * Defaults to "left"
     */
    animateEntryDirection: PropTypes.oneOf([
        'left',
        'right',
        'upwards',
        'downwards',
    ]),
};

export default AttendeeTicketNotification;
